import { CommonModule } from '@angular/common';
import { Component, OnInit, input, inject, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  ValidatorFn,
} from '@angular/forms';
import {
  authorizationFn,
  UserInfoService,
} from '@softline/auth';
import { SoftAppsUserInfo } from '@softapps/core';
import { Getter, parseIso8601, Store } from '@softline/core';
import { DynamicModule, FieldOkResultConverter } from '@softline/dynamic';
import {
  Validators,
  ResolvableModal,
  MessageBarService,
  translator,
  ValueConverterDirective,
} from '@softline/ui-core';
import { Observable } from 'rxjs';
import { TicketType } from '../../data/ticket-type.model';
import { Ticket } from '../../data/ticket.model';
import {
  SOFTLINE_FEATURE_TICKET_TICKET_TYPE,
  SOFTLINE_PERMISSION_TICKET_EDIT_RELEASE,
  SOFTLINE_PERMISSION_TICKET_EDITOR,
  TicketTypeStore,
} from '../../ticket.shared';

import { DateInputComponent } from '@softline/ui-core/date-input';
import { DateTimeInputComponent, DurationInputComponent } from '@softline/ui-core/date-input';
import { InPipe } from '@softline/ui-core';
import { InputComponent } from '@softline/ui-core';
import { IsValidPipe } from '@softline/ui-core';
import { RepeatDirective } from '@softline/ui-core';
import { SelectComponent, SelectOptionDirective, SelectOptionHeaderDirective, SelectOptionSeparatorDirective } from '@softline/ui-core/combo-box';
import { TranslatePipe } from '@softline/ui-core';
import { ValidationContainerComponent } from '@softline/ui-core';
import { ValidationMessagePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-edit-ticket-dialog',
  imports: [
    CommonModule,
    DynamicModule,
    ReactiveFormsModule,
    DateInputComponent,
    DateTimeInputComponent,
    DurationInputComponent,
    InPipe,
    InputComponent,
    IsValidPipe,
    RepeatDirective,
    SelectComponent,
    SelectOptionDirective,
    TranslatePipe,
    ValidationContainerComponent,
    ValidationMessagePipe,
    ValueConverterDirective,
    SelectOptionSeparatorDirective,
    SelectOptionHeaderDirective,
  ],
  templateUrl: './edit-ticket-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./edit-ticket-dialog.component.css'],
})
export class EditTicketDialogComponent extends ResolvableModal<any> implements OnInit {
  private store = inject(Store);
  private fb = inject(UntypedFormBuilder);
  private messageBarService = inject(MessageBarService);
  private readonly userInfo = inject<UserInfoService<SoftAppsUserInfo>>(UserInfoService);
  private t = translator();
  private readonly isAuthorized = authorizationFn();

  ticket = input.required<Ticket>();

  cobitkatValueConverter = new FieldOkResultConverter({
    name: 'cobitkat',
    description: 'bezeichnung',
  });
  objektValueConverter = new FieldOkResultConverter({
    number: 'objekt',
    name: 'bezeichnung',
  });
  costcenterValueConverter = new FieldOkResultConverter({
    number: 'kostenstelle',
    name: 'kontotext1',
  });

  readonly isEditor = this.isAuthorized(SOFTLINE_PERMISSION_TICKET_EDITOR);
  readonly isReleaseEditor = this.isAuthorized(SOFTLINE_PERMISSION_TICKET_EDIT_RELEASE);

  ticketTypes$: Observable<readonly TicketType[]> = this.store.observe(
    SOFTLINE_FEATURE_TICKET_TICKET_TYPE,
    TicketTypeStore.getters.all as Getter<any, undefined, TicketType[]>
  );

  //_ticket?: Ticket = undefined;

  form = this.fb.group({
    name: [undefined, Validators.required()],
    priority: [undefined, Validators.required()],
    type: [undefined, Validators.required()],
    deadline: [
      undefined,
      //[Validators.required(), this.deadlineValidator()]
    ],
    object: [undefined],
    costCenter: [undefined],
    cobitCategory: [undefined],
    rfcCategory: [undefined],
    release: this.fb.group({
      releasestart_beta: [undefined],
      releaseduration_beta: [undefined],
      releasestart_original: [undefined],
      releaseduration_original: [undefined],
    }),
  });

  deadlineValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const deadline = this.ticket()?.deadline;
      const deadlineTime = deadline ? parseIso8601(deadline) : Date.now();
      const valueTime = parseIso8601(control.value);
      if (control.value && deadlineTime !== null && valueTime !== null && deadlineTime > valueTime) {
        return {
          deadline: {
            isValid: false,
            messages: [
              {
                type: 'error',
                subject: '#TICKETS.DIALOGS.EDIT.ERRORS.MOVE_DEADLINE_BACKWARDS',
              },
            ],
          },
        };
      }
      if (control.value && valueTime !== null && Date.now() > valueTime) {
        return {
          deadline: {
            isValid: false,
            messages: [
              {
                type: 'error',
                subject: '#TICKETS.DIALOGS.EDIT.ERRORS.DEADLINE_LESSER_THAN_TODAY',
              },
            ],
          },
        };
      }
      return null;
    };
  }

  ngOnInit(): void {
    const value = this.ticket();
    if (!value) {
      return;
    }
    this.form.patchValue({
      name: value.name,
      priority: value.priority,
      type: value.type?.id,
      deadline: value.deadline ? value.deadline.substring(0, 10) : undefined,
      object: value.object,
      costCenter: value.costCenter,
      cobitCategory: value.cobitCategory,
      rfcCategory: value.rfcCategory,
      release: value.release,
    });
  }

  async loadTicketTypes(): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_TICKET_TICKET_TYPE,
      TicketTypeStore.actions.loadMany
    );
  }

  getAccountId(): number | undefined {
    return this.userInfo.value()?.accountid;
  }

  private getTicketType(id: number | string): Readonly<TicketType> {
    return this.store.get(
      SOFTLINE_FEATURE_TICKET_TICKET_TYPE,
      TicketTypeStore.getters.entity as Getter<any, string, TicketType>,
      '' + id
    );
  }

  async onSubmit(): Promise<void> {
    if (this.form.value.priority && this.form.value.deadline && this.ticket()) {
      //const ticket = { ...this._ticket };
      const ticket = this.ticket();
      ticket.priority = this.form.value.priority;
      ticket.deadline = this.form.value.deadline;

      if (this.isAuthorized(SOFTLINE_PERMISSION_TICKET_EDITOR)()) {
        ticket.name = this.form.value.name;
        ticket.object = this.form.value.object?.id
          ? { id: this.form.value.object?.id }
          : null;
        ticket.costCenter = { id: this.form.value.costCenter?.id };
        ticket.type = this.getTicketType(this.form.value.type);
        ticket.cobitCategory = { id: this.form.value.cobitCategory?.id };
        ticket.rfcCategory = this.form.value.rfcCategory;
      }
      if (
        ticket?.type?.name === 'Release' &&
        this.isAuthorized(SOFTLINE_PERMISSION_TICKET_EDIT_RELEASE)()
      ) {
        ticket.release = this.form.value.release;
      }

      this.resolve(ticket);
    } else {
      this.messageBarService.error('', this.t('#TICKETS.DIALOGS.EDIT_ERROR_MSG'));
    }
  }
}
